import { useCallback, useState } from "react";

export interface CurrentPosition {
  latitude: number;
  longitude: number;
  /** Radius in metres at 95% confidence, as reported by the browser. */
  accuracy: number;
  /** The fix time, from `GeolocationPosition.timestamp`. */
  capturedAt: string;
}

/** i18n keys under the `orders` namespace, one per `GeolocationPositionError` code. */
const ERROR_KEYS: Record<number, string> = {
  1: "orders:locationPermissionDenied",
  2: "orders:locationUnavailable",
  3: "orders:locationTimeout",
};

const GEOLOCATION_OPTIONS: PositionOptions = {
  // Hint that we want GPS rather than network location. Costs battery and latency, but the whole
  // point here is pinning a pickup site.
  enableHighAccuracy: true,
  timeout: 15000,
  // Never hand back a cached fix — the user may have moved since the last one.
  maximumAge: 0,
};

/**
 * One-shot wrapper around `navigator.geolocation.getCurrentPosition`.
 *
 * Requires a secure context, which all our environments are (dev runs HTTPS via mkcert). Never
 * requests on mount: the browser shows a permission prompt, so capture is always user-initiated.
 */
export function useCurrentPosition() {
  const [position, setPosition] = useState<CurrentPosition | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isSupported = typeof navigator !== "undefined" && "geolocation" in navigator;

  const request = useCallback((): Promise<CurrentPosition | null> => {
    if (!isSupported) return Promise.resolve(null);

    setIsLoading(true);
    setErrorKey(null);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        ({ coords, timestamp }) => {
          const next: CurrentPosition = {
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: coords.accuracy,
            capturedAt: new Date(timestamp).toISOString(),
          };
          setPosition(next);
          setIsLoading(false);
          resolve(next);
        },
        (error) => {
          setErrorKey(ERROR_KEYS[error.code] ?? "orders:locationUnavailable");
          setIsLoading(false);
          resolve(null);
        },
        GEOLOCATION_OPTIONS
      );
    });
  }, [isSupported]);

  const reset = useCallback(() => {
    setPosition(null);
    setErrorKey(null);
  }, []);

  return { request, reset, position, accuracy: position?.accuracy ?? null, errorKey, isLoading, isSupported };
}

export default useCurrentPosition;
